/**
 * The two questions this app asks its judge, written once.
 *
 * ONE place, and the evaluation harness imports it: a calibration study that
 * measured a paraphrase of the question the product sends would be a study of
 * something the product does not do. Every threshold anyone later chooses for
 * these answers has to be chosen against the exact wording below, so the wording
 * lives where both the product and the instrument read it.
 *
 * Both follow the shape TypeSafe's own guidance asks for: one narrow judgment per
 * question, over a state with named parts, with the meaning of each answer spelt
 * out in the criteria rather than left to the label. Neither question asks the
 * judge whether a claim is TRUE. The first asks whether a search could bear on
 * it; the second asks what a quoted sentence says about it. Truth is not a
 * question this app puts to any model.
 */

import { foldText } from '../core/similarity'
import { QUOTE_DISPUTES, QUOTE_NEITHER, QUOTE_SUPPORTS } from '../core/stanceMeaning'
import type { ModelStamp, WebSource } from '../core/types'
import { hostOf } from '../core/url'
import { choice, noul } from './judge'
import type { ChoiceAnswer, JudgeJson, JudgeQuestion, NoulAnswer } from './judge'

/* ── checkability ─────────────────────────────────────────────────────── */

export interface CheckabilityClaim {
  id: string
  label: string
  claim: string
}

/**
 * Whether published sources could confirm or dispute a claim as stated.
 *
 * The question a run has to answer before it spends a source check — nine
 * billed web requests, the dearest pass it makes — on a claim. `checkableClaim`
 * in `runPolicy.ts` no longer tries to: it refuses only experiments and
 * conjectures, because twenty measured checks showed its digit test throwing away
 * the checks worth making (D364). This is a stand-in for the question, and it
 * stays one: it orders candidates and never removes one (D106).
 *
 * The yes and no are the verify prompt's own distinction between a claim a
 * search can settle and one it cannot — `not-checkable` there is "a derivation,
 * a definition, a conditional, or a judgement about this particular graph".
 */
export function checkabilityQuestions(claims: readonly CheckabilityClaim[]): {
  state: JudgeJson
  questions: Record<string, JudgeQuestion>
} {
  const state: JudgeJson = {
    claims: claims.map((c) => ({ id: c.id, label: c.label, claim: c.claim })),
  }
  const questions: Record<string, JudgeQuestion> = {}
  claims.forEach((_, i) => {
    questions[`c${i}`] = noul(
      `Could a web search find published sources that confirm or dispute the claim at \`claims[${i}].claim\`, as it is stated? Judge the claim on its own words, not whether it is true.`,
      {
        true: 'The claim asserts something about the world that published evidence could bear on: an empirical finding, a measurement, a quantity, a historical fact, a property of a material or a system, a documented practice, or a comparison between them.',
        false:
          'The claim is a step inside an argument rather than a fact about the world: a derivation from the claims above it, a definition, a conditional, a proposal, a plan, an open question, or a judgement about how this particular line of reasoning is going.',
      },
    )
  })
  return { state, questions }
}

/** The name the checkability answer for the i-th claim comes back under. */
export const checkabilityKey = (i: number): string => `c${i}`

/** P(a search could settle it), read off the answer, or null for a missing one. */
export const checkability = (answers: Record<string, unknown>, i: number): number | null => {
  const a = answers[checkabilityKey(i)] as NoulAnswer | undefined
  return a && a.type === 'noul' ? a.noul : null
}

/* ── stance ───────────────────────────────────────────────────────────── */

/** What the judge is offered for a quote, and what each answer means. */
export const STANCE_LABELS = ['supports', 'disputes', 'neither'] as const
export type JudgedStance = (typeof STANCE_LABELS)[number]

/** How many characters of a page's own text travel either side of the quote. */
export const EXCERPT_RADIUS = 600

export interface StanceSource {
  title: string
  host: string
  quote: string
  /** A window of the page's own text around the quote, where the page returned
   *  text. Absent where it did not — the judge then reads the quote alone, and the
   *  record says so. */
  excerpt?: string
}

/**
 * What a quoted sentence says about a claim: the one judgment in a source check
 * that nothing mechanical checks.
 *
 * `reconcile` tests whether the quote is on the page, whether two pages are one
 * artefact and whether two domains are one owner. Whether the quoted words
 * actually dispute the claim is the reasoning model's own `stance`, read through
 * no gate. This asks a second, non-generating reader the same question over the
 * same words and stores the answer beside the first, stamped (D106). It reaches
 * no gate either: `refutedIsEarned` never reads it, by test.
 */
export function stanceQuestions(
  claim: string,
  sources: readonly StanceSource[],
): { state: JudgeJson; questions: Record<string, JudgeQuestion> } {
  const state: JudgeJson = {
    claim,
    sources: sources.map((s) => ({
      title: s.title,
      host: s.host,
      quote: s.quote,
      ...(s.excerpt ? { excerpt: s.excerpt } : {}),
    })),
  }
  const questions: Record<string, JudgeQuestion> = {}
  sources.forEach((s, i) => {
    questions[`s${i}`] = choice(
      s.excerpt
        ? `What does the quoted sentence at \`sources[${i}].quote\` say about \`claim\`? The surrounding page text at \`sources[${i}].excerpt\` is there only to read the quote in context.`
        : `What does the quoted sentence at \`sources[${i}].quote\` say about \`claim\`?`,
      // The check model is held to the same three (D379).
      { supports: QUOTE_SUPPORTS, disputes: QUOTE_DISPUTES, neither: QUOTE_NEITHER },
    )
  })
  return { state, questions }
}

/** The name the stance answer for the i-th source comes back under. */
export const stanceKey = (i: number): string => `s${i}`

/** The judge's reading of the i-th source, or null for a missing answer. */
export const judgedStance = (
  answers: Record<string, unknown>,
  i: number,
): { stance: JudgedStance; probabilities: Record<JudgedStance, number> } | null => {
  const a = answers[stanceKey(i)] as ChoiceAnswer | undefined
  if (!a || a.type !== 'choice') return null
  const stance = STANCE_LABELS.find((l) => l === a.choice)
  if (!stance) return null
  return {
    stance,
    probabilities: {
      supports: a.probabilities.supports ?? 0,
      disputes: a.probabilities.disputes ?? 0,
      neither: a.probabilities.neither ?? 0,
    },
  }
}

/** The app's own stance vocabulary for a judged one. `neither` is `context`. */
export const stanceOf = (j: JudgedStance): WebSource['stance'] => (j === 'neither' ? 'context' : j)

/**
 * A window of a page's own text around the quote, or null when the quote is not
 * in the text this app received.
 *
 * Over the same FOLDED strings `reconcile` compares, so "in the text" here means
 * exactly what `quoteFound === true` means there. The window is what makes the
 * judge's reading a reading in context rather than of a sentence on its own,
 * and its radius is bounded because a whole page is more state than the
 * question needs and more than a request may carry.
 */
export function excerptAround(foldedPage: string, foldedQuote: string): string | null {
  if (!foldedQuote) return null
  const at = foldedPage.indexOf(foldedQuote)
  if (at < 0) return null
  const start = Math.max(0, at - EXCERPT_RADIUS)
  const end = Math.min(foldedPage.length, at + foldedQuote.length + EXCERPT_RADIUS)
  return `${start > 0 ? '…' : ''}${foldedPage.slice(start, end)}${end < foldedPage.length ? '…' : ''}`
}

/* ── attaching a second reading to a check ────────────────────────────── */

/** The sources of a check that carry a quote, paired with what the judge is
 *  handed for each: an index back into the check's list, and the text. */
export interface StanceInput {
  index: number
  source: StanceSource
}

/**
 * What to ask the judge about, out of a check's admitted sources.
 *
 * Only sources with a quote: there is nothing to read a stance off otherwise.
 * `pageText` is the fetched text the harvest kept for a URL — the same text the
 * quote check read — and an excerpt is cut from it only where the folded quote
 * is really in the folded page, so an excerpt never contradicts `quoteFound`.
 */
export function stanceInputsFor(
  sources: readonly WebSource[],
  pageText: (url: string) => string | undefined,
): StanceInput[] {
  const out: StanceInput[] = []
  sources.forEach((s, index) => {
    if (!s.quote.trim()) return
    const raw = pageText(s.url)
    const excerpt = raw ? excerptAround(foldText(raw), foldText(s.quote)) : null
    out.push({
      index,
      source: {
        title: s.title,
        host: hostOf(s.url),
        quote: s.quote,
        ...(excerpt ? { excerpt } : {}),
      },
    })
  })
  return out
}

/**
 * The check's sources with the judge's readings attached, by position.
 *
 * A NEW array: `reconcile`'s output is not mutated, and a source the judge did
 * not answer for keeps no `judged` at all rather than a placeholder. Nothing
 * else on the source changes — the stance the model gave stays the stance, and
 * every field the gate reads is exactly what it was.
 */
export function withJudgedStances(
  sources: readonly WebSource[],
  inputs: readonly StanceInput[],
  answers: Record<string, unknown>,
  model: ModelStamp,
): WebSource[] {
  const byIndex = new Map<number, NonNullable<WebSource['judged']>>()
  inputs.forEach((input, i) => {
    const j = judgedStance(answers, i)
    if (j) byIndex.set(input.index, { stance: j.stance, probabilities: j.probabilities, model })
  })
  return sources.map((s, i) => {
    const judged = byIndex.get(i)
    return judged ? { ...s, judged } : s
  })
}
